/**
 * useWizardTiming — how long each wizard step takes, which field holds focus longest, and how
 * long the whole run takes. Answers "where does the form create friction?" so the AI-assist work
 * can target the slowest steps/fields.
 *
 * Events: WIZARD_STARTED (once per run), WIZARD_STEP_EXIT {step_name, exit_type, duration_sec},
 * WIZARD_FIELD_TIME {step_name, field_name, duration_sec}. The run total is attached to
 * INVITATION_PUBLISHED (see api.ts) via wizardSession.consume().
 *
 * Only VISIBLE time counts (a backgrounded tab is not "engaged"), and events under 1s are dropped.
 */
import { useEffect, useRef } from 'react';
import { trackEvent } from '@/src/infrastructure/lib/analytics';
import {
  ActiveTimer,
  type FieldTimingStop,
  installWizardFieldTiming,
  roundedSeconds,
  slug,
  wizardSession,
} from '@/src/infrastructure/lib/behaviour-analytics';

type Exit = 'forward' | 'back' | 'leave';

export function useWizardTiming(step: number, stepName: string): void {
  const stepNameRef = useRef(stepName);
  stepNameRef.current = stepName;
  const current = useRef<{ step: number; name: string; timer: ActiveTimer } | null>(null);
  const fields = useRef<FieldTimingStop | null>(null);

  const emitExit = (exitType: Exit) => {
    const cur = current.current;
    if (!cur) return;
    cur.timer.stop();
    current.current = null;
    const sec = roundedSeconds(cur.timer.seconds());
    if (sec < 1) return;
    trackEvent('WIZARD_STEP_EXIT', 'wizard', cur.name, undefined, {
      step_name: cur.name,
      exit_type: exitType,
      duration_sec: sec,
    });
  };

  // Run-level lifecycle: start marker, field timing, tab-visibility and page-hide handling.
  useEffect(() => {
    if (wizardSession.start()) trackEvent('WIZARD_STARTED', 'wizard', slug(stepNameRef.current), undefined, { step_name: slug(stepNameRef.current) });

    const stopFields = installWizardFieldTiming(() => slug(stepNameRef.current));
    fields.current = stopFields;
    const onVisibility = () => {
      const timer = current.current?.timer;
      if (!timer) return;
      if (document.visibilityState === 'visible') timer.start();
      else timer.stop();
    };
    const onPageHide = () => emitExit('leave');
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      stopFields();
      fields.current = null;
      emitExit('leave'); // SPA navigation away from the wizard
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step changes: close the previous step (forward/back), open the new one.
  useEffect(() => {
    const prev = current.current;
    // A focused input that is unmounted by the step change never fires `focusout` — flush it now so
    // its time is attributed to the right field/step, not inflated into the next one.
    if (prev && prev.step !== step) fields.current?.flush();
    wizardSession.touch(); // keeps an in-progress run from being treated as abandoned
    if (prev && prev.step !== step) emitExit(step > prev.step ? 'forward' : 'back');
    if (!current.current) {
      const timer = new ActiveTimer();
      if (document.visibilityState === 'visible') timer.start();
      current.current = { step, name: slug(stepNameRef.current), timer };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
}
